import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Upload, X, Plus, Trash2, Eye, Clock } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { createPost, updatePost, uploadMedia } from "@/lib/adminCmsApi";
import AdminShell from "@/components/admin/AdminShell";
import RichTextEditor from "@/components/admin/RichTextEditor";

const EMPTY = {
  slug: "", title: "", excerpt: "", body: "",
  cover_image_url: "", cover_image_public_id: "",
  tags: [], status: "published",
  published_at: new Date().toISOString(),
};

export default function BlogEditor() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const isNew = postId === "new";
  const [post, setPost] = useState(isNew ? EMPTY : null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/admin/cms/blog`)
      .then((r) => {
        const found = (r.data || []).find((x) => x.id === postId);
        if (found) setPost(found);
        else { toast.error("Post not found"); navigate("/admin/blog"); }
      })
      .catch(() => toast.error("Load failed"))
      .finally(() => setLoading(false));
  }, [postId, isNew, navigate]);

  const set = (k, v) => setPost({ ...post, [k]: v });

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

  const save = async () => {
    if (!post.title?.trim()) { toast.error("Title is required"); return; }
    const slug = (post.slug?.trim() || slugify(post.title));
    setSaving(true);
    try {
      const payload = {
        slug, title: post.title.trim(), excerpt: post.excerpt || "", body: post.body || "",
        cover_image_url: post.cover_image_url || "", cover_image_public_id: post.cover_image_public_id || "",
        tags: post.tags || [], status: post.status || "published",
        published_at: post.published_at || new Date().toISOString(),
      };
      let saved;
      if (isNew) {
        saved = await createPost(payload);
        toast.success("Post created");
        navigate(`/admin/blog/${saved.id}`, { replace: true });
      } else {
        saved = await updatePost(postId, payload);
        toast.success("Saved");
      }
      setPost(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    try {
      const res = await uploadMedia(file, { folder: "ags/blog", alt_text: post.title || "Blog cover" });
      setPost({ ...post, cover_image_url: res.url, cover_image_public_id: res.public_id });
      toast.success("Cover uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  if (loading || !post) {
    return <AdminShell title="Edit post"><div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div></AdminShell>;
  }

  const publishedDate = post.published_at ? new Date(post.published_at) : null;
  const isScheduled = post.status === "published" && publishedDate && publishedDate > new Date();
  const isDraft = post.status === "draft";

  return (
    <AdminShell title={isNew ? "New post" : `Edit · ${post.title}`}>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/blog" className="inline-flex items-center gap-2 text-ags-text-muted hover:text-[#0E2A57] font-mono text-[10px] uppercase tracking-[0.22em]">
          <ArrowLeft size={14} /> Back to posts
        </Link>
        <div className="flex items-center gap-3">
          {(isDraft || isScheduled) && post.slug && !isNew && (
            <a
              href={`/blog/${post.slug}?preview=1`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-draft"
              className="inline-flex items-center gap-2 border border-[#0E2A57]/30 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
            >
              <Eye size={14} /> Preview {isScheduled ? "scheduled" : "draft"}
            </a>
          )}
          {isScheduled && (
            <span data-testid="scheduled-badge" className="inline-flex items-center gap-1.5 bg-[#FFB836] text-ags-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em]">
              <Clock size={11} /> Scheduled · {publishedDate.toLocaleDateString("en-IE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          {isDraft && (
            <span data-testid="draft-badge" className="inline-flex items-center gap-1.5 bg-[#E5EBF2] text-ags-text-muted px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em]">
              Draft
            </span>
          )}
          <button onClick={save} disabled={saving} data-testid="save-post"
                  className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] disabled:opacity-50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left — content */}
        <div className="lg:col-span-8 space-y-4">
          <input type="text" placeholder="Post title…" value={post.title} onChange={(e) => set("title", e.target.value)}
                 className="w-full bg-transparent border-b-2 border-[#0E2A57]/15 px-1 py-3 font-heading font-extrabold text-3xl md:text-4xl tracking-[-0.035em] text-ags-ink focus:border-[#0E2A57] focus:outline-none placeholder:text-ags-text-muted/40" />
          <textarea rows={2} placeholder="Excerpt — short teaser shown in the blog list" value={post.excerpt} onChange={(e) => set("excerpt", e.target.value)}
                    className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 text-base text-ags-text-muted focus:border-[#0E2A57] focus:outline-none" />
          <RichTextEditor value={post.body} onChange={(v) => set("body", v)} placeholder="Start writing your article…" minHeight={420} />
        </div>

        {/* Right — sidebar */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-[#0E2A57]/15 p-5 space-y-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">Publish</div>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">Status</span>
              <select value={post.status} onChange={(e) => set("status", e.target.value)}
                      className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">Slug (URL)</span>
              <input type="text" value={post.slug} onChange={(e) => set("slug", e.target.value)}
                     placeholder={slugify(post.title || "")}
                     className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 font-mono text-sm focus:border-[#0E2A57] focus:outline-none" />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">Published at</span>
              <input type="datetime-local" value={(post.published_at || "").slice(0, 16)} onChange={(e) => set("published_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                     data-testid="published-at"
                     className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 text-sm" />
              <div className="mt-1.5 text-xs text-ags-text-muted">
                Set a future date with status <strong>Published</strong> to <strong>schedule</strong> the post. It will go live automatically.
              </div>
            </label>
          </div>

          <div className="bg-white border border-[#0E2A57]/15 p-5 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">Cover Image</div>
            {post.cover_image_url ? (
              <div className="relative">
                <img src={post.cover_image_url} alt="" className="w-full aspect-video object-cover border border-ags-hairline" />
                <button onClick={() => setPost({ ...post, cover_image_url: "", cover_image_public_id: "" })}
                        className="absolute top-2 right-2 bg-white border border-[#0E2A57]/20 p-1.5 hover:bg-red-50"><X size={14} /></button>
              </div>
            ) : (
              <div className="w-full aspect-video border-2 border-dashed border-[#0E2A57]/30 flex items-center justify-center text-ags-text-muted text-sm">No cover</div>
            )}
            <label className="cursor-pointer flex items-center justify-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors">
              <Upload size={12} /> {post.cover_image_url ? "Replace" : "Upload cover"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} />
            </label>
          </div>

          <div className="bg-white border border-[#0E2A57]/15 p-5 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">Tags</div>
            <div className="space-y-2">
              {(post.tags || []).map((t, i) => (
                <div key={i} className="flex gap-2">
                  <input value={t} onChange={(e) => { const n = [...post.tags]; n[i] = e.target.value; set("tags", n); }}
                         className="flex-1 bg-white border border-[#0E2A57]/15 px-3 py-1.5 text-sm" />
                  <button onClick={() => { const n = [...post.tags]; n.splice(i, 1); set("tags", n); }} className="px-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => set("tags", [...(post.tags || []), ""])} className="inline-flex items-center gap-1 bg-[#0E2A57] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]">
              <Plus size={10} /> Add tag
            </button>
          </div>
        </aside>
      </div>
    </AdminShell>
  );
}
